import { bgNegative } from '@osmosis/tailwind-ui/styles/background';
import { text0 } from '@osmosis/tailwind-ui/styles/text';
import { Box } from '@osmosis/tailwind-ui/components/Box';
import { Flex } from '@osmosis/tailwind-ui/components/Box';
import { Button } from '@osmosis/tailwind-ui/components/Button';
import { P } from '@osmosis/tailwind-ui/components/Text';
import { H4 } from '@osmosis/tailwind-ui/components/Text';

import React from 'react';
import { faGlobeAmericas } from '@fortawesome/pro-regular-svg-icons/faGlobeAmericas';
import { BookReferenceProps } from '@osmosis/shared/types/learndb/learnData';

import LearnBlock, { CommonLearnBlockProps } from './LearnBlock';
import { useIntl } from 'react-intl';

type Props = {
  selectedReference: BookReferenceProps;
  references: BookReferenceProps[];
  onSelectReference: (r: BookReferenceProps) => void;
} & CommonLearnBlockProps;

const parsePageNumber = (str: string): string => {
  const [text, pages] = str.split('|');
  const formattedText = pages ? `${text} p. ${pages}` : text;

  return formattedText;
};

/**
 * Renders the External References learn block with year selector and reference list.
 */
const ExternalReferencesBlockView = (props: Props): JSX.Element => {
  const { selectedReference, references, onSelectReference, contentOnly } =
    props;
  const intl = useIntl();

  // Moves focus and selection to a year chip by its index, wrapping at both ends.
  const getWrappedIndex = (current: number, delta: number): number =>
    (current + delta + references.length) % references.length;

  const focusYearButton = (bookYear: number): void => {
    document
      .querySelector<HTMLElement>(
        `[data-testid="first-aid-year-${bookYear}-button"]`,
      )
      ?.focus();
  };

  const handleYearKeyDown =
    (r: BookReferenceProps, index: number) =>
    (e: React.KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelectReference(r);
      } else if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        const next = references[getWrappedIndex(index, 1)];
        onSelectReference(next);
        focusYearButton(next.bookYear);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        const prev = references[getWrappedIndex(index, -1)];
        onSelectReference(prev);
        focusYearButton(prev.bookYear);
      }
    };

  const content = (
    <Box className="[&_a]:text-primary [&_a]:no-underline">
      <Flex className={`items-start mb-4`}>
        <Box className={`mr-2 p-2 ${bgNegative} rounded-md`}>
          <H4 cn={text0}>{selectedReference.bookTitle}</H4>
        </Box>

        <Box
          role="radiogroup"
          aria-label={intl.formatMessage({
            id: 'learn_block.external_references.year_group',
            defaultMessage: 'Reference year',
          })}
          className={`flex flex-1 flex-wrap`}
        >
          {references.map((r, index) => {
            const isSelected = r.bookID === selectedReference.bookID;

            return (
              <Button
                key={r.bookID}
                variant="transparent"
                cn={`!p-2 !rounded-md !font-normal !justify-start flex-wrap mb-1 mx-2 cursor-pointer ${
                  isSelected ? 'bg-gray-300 hover:bg-gray-400' : ''
                }`}
                role={
                  'radio' /* NOSONAR: button+role="radio" is valid ARIA per OSMS-18135; native <input type="radio"> refactor tracked separately */
                }
                aria-checked={isSelected}
                onClick={() => onSelectReference(r)}
                onKeyDown={handleYearKeyDown(r, index)}
                data-testid={`first-aid-year-${r.bookYear}-button`}
              >
                <P>{r.bookYear}</P>
              </Button>
            );
          })}
        </Box>
      </Flex>
      <Flex className={`flex-wrap`}>
        {selectedReference.data.map((r) => (
          <Box key={r.ref} className="w-full md:w-1/3 m-4">
            <H4 dangerouslySetInnerHTML={{ __html: parsePageNumber(r.ref) }} />
            {r.group?.map((item) => (
              <Box key={item} className={`pl-4`}>
                <P
                  dangerouslySetInnerHTML={{ __html: parsePageNumber(item) }}
                />
              </Box>
            ))}
          </Box>
        ))}
      </Flex>
    </Box>
  );

  return (
    <LearnBlock
      title={intl.formatMessage({
        id: 'learn_block.external_references',
        defaultMessage: 'External References',
      })}
      icon={faGlobeAmericas}
      content={content}
      isLockoutBlock
      resource="External References block"
      contentOnly={contentOnly}
    />
  );
};

export default ExternalReferencesBlockView;
